/**
 * Geo-Targeting helper utility.
 * Detects visitor country via reliable, lightweight Geo-IP services with caching.
 */

export const getCachedCountry = () => {
    if (typeof window === 'undefined') return null;
    try {
        const params = new URLSearchParams(window.location.search);
        const override = params.get('geo') || params.get('country');
        if (override) return override.trim().toUpperCase();

        return sessionStorage.getItem('user_geo_country') || null;
    } catch (e) {
        return null;
    }
};

const fetchWithTimeout = async (url, timeoutMs = 2500) => {
    if (typeof AbortController === 'undefined') {
        return fetch(url);
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timer);
        return res;
    } catch (err) {
        clearTimeout(timer);
        throw err;
    }
};

export const detectCountry = async () => {
    const cached = getCachedCountry();
    if (cached) return cached;

    if (typeof window === 'undefined' || typeof fetch === 'undefined') {
        return 'UNKNOWN';
    }

    // 1. Primary provider: api.country.is (fast CDN-backed, no key required)
    try {
        const res = await fetchWithTimeout('https://api.country.is/');
        if (res.ok) {
            const data = await res.json();
            if (data && data.country) {
                const code = String(data.country).trim().toUpperCase();
                try { sessionStorage.setItem('user_geo_country', code); } catch (e) {}
                return code;
            }
        }
    } catch (e) {
        // Fall through to backup
    }

    // 2. Secondary provider: ipwho.is (CORS enabled, generous free tier)
    try {
        const res = await fetchWithTimeout('https://ipwho.is/');
        if (res.ok) {
            const data = await res.json();
            if (data && data.country_code) {
                const code = String(data.country_code).trim().toUpperCase();
                try { sessionStorage.setItem('user_geo_country', code); } catch (e) {}
                return code;
            }
        }
    } catch (e) {
        // Fall through to backup
    }

    // 3. Tertiary provider: ipapi.co
    try {
        const res = await fetchWithTimeout('https://ipapi.co/json/');
        if (res.ok) {
            const data = await res.json();
            if (data && data.country_code) {
                const code = String(data.country_code).trim().toUpperCase();
                try { sessionStorage.setItem('user_geo_country', code); } catch (e) {}
                return code;
            }
        }
    } catch (e) {
        // Fall through
    }

    return 'UNKNOWN';
};

export const isRussianCountry = (code) => {
    return Boolean(code && String(code).trim().toUpperCase() === 'RU');
};
